import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';

const AboutUsPage: React.FC = () => {
  const VALUES = [
    { id: 1, title: "Clinical integrity", description: "We maintain the highest standards of clinical accuracy and safety in all our recommendations." },
    { id: 2, title: "Data-driven decision-making", description: "Our platform transforms complex laboratory data into actionable insights backed by evidence." },
    { id: 3, title: "AI for social good", description: "We harness artificial intelligence to improve healthcare outcomes and combat antimicrobial resistance." },
    { id: 4, title: "Transparency and accountability", description: "We are committed to clear communication about our methods, limitations, and continuous improvement." },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="w-full bg-gradient-to-br from-primary/90 to-secondary/90 text-primary-light py-10 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 text-primary">
            About ReasonShield
          </h1>
          <p className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto opacity-90 text-primary">
            Making lab data actionable—turning complex molecular and pharmacogenomic insights into clear, AI-assisted treatment pathways.
          </p>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Company Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="prose prose-lg max-w-none mb-10 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-primary text-center">Our Company</h2>
          <p className="text-base sm:text-lg text-text">
            <strong>ReasonShield</strong> is an advanced clinical decision support platform developed by <strong>ReasonWorks AI Inc</strong>, designed to assist healthcare professionals in interpreting laboratory results and optimizing treatment decisions. Our comprehensive platform includes both molecular microbiology analysis for antimicrobial treatments and pharmacogenomics (PGx) testing for personalized medicine. Our goal is to bridge the gap between complex laboratory data and timely, evidence-based treatment decisions.
          </p>
          <p className="text-base sm:text-lg text-text mt-3 sm:mt-4">
            The ReasonShield platform was developed by a team led by Baljindersingh Bedi, an Artificial Intelligence Engineer with expertise in medical data systems, algorithmic decision-making, and laboratory informatics. Our dual-platform approach includes:
          </p>
          <div className="mt-3 sm:mt-4 mb-3 sm:mb-4">
            <p className="text-base sm:text-lg text-text font-semibold">Molecular Platform:</p>
            <p className="text-base sm:text-lg text-text">Antimicrobial treatment recommendations powered by AI algorithms rigorously trained and aligned with authoritative sources, including:</p>
          </div>
          <ul className="list-disc pl-5 sm:pl-6 mt-3 sm:mt-4 space-y-1 sm:space-y-2">
            <li className="text-base sm:text-lg text-text">The <strong>Sanford Guide to Antimicrobial Therapy</strong></li>
            <li className="text-base sm:text-lg text-text">The <strong>Centers for Disease Control and Prevention (CDC)</strong></li>
            <li className="text-base sm:text-lg text-text">The <strong>World Health Organization (WHO)</strong></li>
            <li className="text-base sm:text-lg text-text">Adverse event datasets from the <strong>FDA's OpenFDA platform</strong></li>
          </ul>
          <div className="mt-3 sm:mt-4 mb-3 sm:mb-4">
            <p className="text-base sm:text-lg text-text font-semibold">PGx Platform:</p>
            <p className="text-base sm:text-lg text-text">Pharmacogenomic analysis providing personalized medication insights based on genetic variations affecting drug metabolism, efficacy, and adverse reactions. Available at <a href="https://pgx.reasonshield.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-accent underline">pgx.reasonshield.com</a>.</p>
          </div>
          <p className="text-base sm:text-lg text-text mt-3 sm:mt-4">
          To ensure clinical accuracy, both platforms are developed in close collaboration with a <strong>CAP-accredited Molecular Laboratory Consultant</strong> who also serves as director and advisor to several high-complexity laboratories across the country.          </p>
          <p className="text-base sm:text-lg text-text mt-3 sm:mt-4">
            We do not replace clinical judgment. ReasonShield is a comprehensive support system for licensed healthcare providers, helping them make faster, more data-informed decisions—whether for antimicrobial treatments or personalized medication selection—with clarity and confidence.
          </p>
        </motion.div>

        {/* Core Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-10 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-8 text-primary text-center">Our Core Values</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
            {VALUES.map((value) => (
              <div key={value.id} className="bg-background-alt p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-lg sm:text-xl font-semibold text-primary mb-2 sm:mb-3">{value.title}</h3>
                <p className="text-sm sm:text-base text-text">{value.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Founder Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-10 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-primary text-center">Our Story</h2>
          <div className="bg-background-alt p-4 sm:p-8 rounded-lg shadow-md border-l-4 border-primary">
            <p className="text-base sm:text-lg text-text italic">
              "ReasonShield began as a passion project to address a growing gap between advanced lab testing and actionable treatment guidance. With a background in AI and deep respect for clinical expertise, I built this tool to empower providers—not replace them."
            </p>
            <p className="text-right mt-3 sm:mt-4 font-medium text-primary text-sm sm:text-base">— Baljindersingh Bedi, Founder</p>
          </div>
        </motion.div>

        {/* Future Direction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mb-10 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-primary text-center">Validation & Future Direction</h2>
          <p className="text-base sm:text-lg text-text">
            We are continuously improving our models with feedback from clinicians and ongoing updates from major public health organizations. Our roadmap includes integration with EMRs and personalized susceptibility pattern analysis.
          </p>
        </motion.div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-background-alt p-4 sm:p-6 rounded-lg border border-secondary mb-10 sm:mb-16"
        >
          <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 text-primary">Important Disclaimer</h3>
          <p className="text-sm sm:text-base text-text">
            The antimicrobial treatment recommendations presented in this report are generated using an AI-based algorithm developed in accordance with authoritative clinical references, including the Sanford Guide,
            Infectious Diseases Society of America (IDSA) guidelines, Centers for Disease Control and Prevention (CDC) protocols, and real-world adverse event data from the FDA's OpenFDA database. These
            recommendations are intended solely to support licensed healthcare professionals in their clinical decision-making. Use of this report is at the discretion of the healthcare provider, and the authors and platform
            assume no liability for treatment decisions based on this guidance.
          </p>
          <p className="text-sm sm:text-base text-text mt-2 italic">
            Note: Clinical correlation is required.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center"
        >
          <h2 className="text-xl sm:text-2xl font-bold text-text mb-4 sm:mb-6">Ready to Experience ReasonShield?</h2>
          <Button variant="primary" size="md" className="w-full sm:w-auto" asChild={true}>
            <Link to="/contact">Contact Us Today</Link>
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutUsPage; 